import * as React from 'react';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import { resolve } from 'inversify-react';

import { TaxonomyDataService } from 'src/app/general/taxonomies/form/taxonomy-form.data';
import { TaxonomyTagsEditorView } from './taxonomy-tags-editor.view';
import { DataLocalService } from 'src/core/services/data-local.service';
import { ConfigurationService } from 'src/core/services/configuration.service';

interface Props {
	taxonomyDefinitionName: string | null;
	mode: ControlMode;
	multi?: boolean;
	allowNewTags?: boolean;
	value: string | string[];
	onChange: (value: TaxonomyTagSummary[] | TaxonomyTagSummary) => void;
	setBusy: (isBusy: boolean) => void;
	label?: string;
	required?: boolean;
	onGetErrorMessage?: () => Promise<ValidationError[]>;
}

interface State {
	defaultLanguage: string | null;
	tags: TaxonomyTagSummary[];
}

@observer
export class TaxonomyTagsEditorController extends React.Component<Props, State> {
	@observable
	tagsDataService: DataLocalService<TaxonomyTagSummary>;

	@resolve
	configurationService: ConfigurationService;

	constructor(props: Readonly<Props>) {
		super(props);

		this.state = {
			defaultLanguage: null,
			tags: null
		};
	}

	componentDidMount() {
		this.initialize(this.props.taxonomyDefinitionName);
	}

	componentWillReceiveProps(nextProps: Readonly<Props>) {
		if (this.props.taxonomyDefinitionName !== nextProps.taxonomyDefinitionName) {
			this.initialize(nextProps.taxonomyDefinitionName);
		}
	}

	private async loadConfiguration(): Promise<void> {
		const appConfiguration = await this.configurationService.current();
		this.setState({
			defaultLanguage: appConfiguration.defaultLanguage
		});
	}

	private async loadData(jsonSchemaDefinitionName: string): Promise<void> {
		const taxonomyDataService = TaxonomyDataService.make();
		this.tagsDataService = new DataLocalService([]);
		this.setState({ tags: null });

		taxonomyDataService.addFilter('jsonSchemaDefinitionName', 'eq', jsonSchemaDefinitionName);
		taxonomyDataService.addSorting('id', 'asc', true);
		taxonomyDataService.pageSize = 1;

		taxonomyDataService.load(query => query)
			.then((items) => {
				const taxonomy = items[0];
				const tags = taxonomy.dynamicProperties.taxonomyTags.map((m) => {
					const languages = Object.getOwnPropertyNames(m.displayName);
					let index = languages.indexOf(this.state.defaultLanguage);
					if (index === -1) {
						index = 0;
					}
					const language = languages[index];

					return {
						id: m.id,
						displayName: m.displayName[language]
					};
				});
				this.setState({ tags: [...tags] });
			});

		this.tagsDataService.setQuery(() => {
			const keywords = this.tagsDataService.searchKeywords;
			let tags = this.state.tags || [];
			if (keywords) {
				tags = tags.filter(m => m.displayName.indexOf(keywords) > -1);
			}

			return tags;
		});
	}

	private initialize(jsonSchemaDefinitionName: string): void {
		this.loadConfiguration()
			.then(() => this.loadData(jsonSchemaDefinitionName));
	}

	private get request(): DropdownRequest<TaxonomyTagSummary> {
		return {
			query: async (): Promise<QueryResult<TaxonomyTagSummary>> => {
				this.tagsDataService.currentPage = 1;
				this.tagsDataService.pageSize = 100;
				await this.tagsDataService.load();
				return {
					items: this.tagsDataService.items,
					totalCount: this.tagsDataService.totalPages
				};
			},
			dataSet: this.tagsDataService,
		};
	}

	render(): React.ReactNode {
		const { tags } = this.state;
		if (tags == null) {
			return null;
		}

		return (
			<TaxonomyTagsEditorView
				request={this.request}
				{...this.props}
			/>
		);
	}
}



// WEBPACK FOOTER //
// ./src/app/general/taxonomies/parts/taxonomy-tags-editor.controller.tsx